/**
 * Title Folders Browse Page
 *
 * Search and filter title folders with advanced filtering options.
 * Allows browsing by location state, audit status, and bucket.
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FolderCheck,
  FolderX,
  FolderArchive,
  RotateCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Layers,
  FileStack,
  HardDrive,
} from "lucide-react";
import {
  searchTitleFolders,
  formatBytes,
  formatRelativeTime,
  type TitleFolderIndexRecord,
  type LocationState,
  type AuditStatus,
} from "@/lib/title-folder-api";
import { TitleFolderLayout } from "./layout";

const ITEMS_PER_PAGE = 25;

const LOCATION_STATES: { value: LocationState | "all"; label: string; icon: typeof FolderCheck }[] = [
  { value: "all", label: "All Locations", icon: Layers },
  { value: "InSharePoint", label: "In SharePoint", icon: FolderCheck },
  { value: "Archived", label: "Archived", icon: FolderArchive },
  { value: "Missing", label: "Missing", icon: FolderX },
  { value: "Restoring", label: "Restoring", icon: RotateCw },
];

const AUDIT_STATUSES: { value: AuditStatus | "all"; label: string }[] = [
  { value: "all", label: "All Statuses" },
  { value: "Valid", label: "Valid" },
  { value: "NamingMismatch", label: "Naming Mismatch" },
  { value: "WrongBucket", label: "Wrong Bucket" },
  { value: "Orphan", label: "Orphan" },
];

function getLocationIcon(state: LocationState | null) {
  switch (state) {
    case "InSharePoint":
      return <FolderCheck className="h-4 w-4 text-emerald-500" />;
    case "Archived":
      return <FolderArchive className="h-4 w-4 text-blue-500" />;
    case "Missing":
      return <FolderX className="h-4 w-4 text-red-500" />;
    case "Restoring":
      return <RotateCw className="h-4 w-4 text-amber-500 animate-spin" />;
    default:
      return <FolderX className="h-4 w-4 text-slate-400" />;
  }
}

function getAuditStatusBadge(status: AuditStatus | null) {
  switch (status) {
    case "Valid":
      return (
        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300 border-0">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Valid
        </Badge>
      );
    case "NamingMismatch":
      return (
        <Badge className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300 border-0">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Naming
        </Badge>
      );
    case "WrongBucket":
      return (
        <Badge className="bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300 border-0">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Wrong Bucket
        </Badge>
      );
    case "Orphan":
      return (
        <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300 border-0">
          <XCircle className="h-3 w-3 mr-1" />
          Orphan
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="text-slate-500">
          Unknown
        </Badge>
      );
  }
}

export default function TitleFoldersBrowsePage() {
  const [folders, setFolders] = useState<TitleFolderIndexRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const { toast } = useToast();

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [locationState, setLocationState] = useState<LocationState | "all">("all");
  const [auditStatus, setAuditStatus] = useState<AuditStatus | "all">("all");
  const [bucket, setBucket] = useState("");

  // Parse URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlLocationState = params.get("locationState") as LocationState | null;
    const urlAuditStatus = params.get("auditStatus") as AuditStatus | null;

    if (urlLocationState && LOCATION_STATES.some(s => s.value === urlLocationState)) {
      setLocationState(urlLocationState);
    }
    if (urlAuditStatus && AUDIT_STATUSES.some(s => s.value === urlAuditStatus)) {
      setAuditStatus(urlAuditStatus);
    }
  }, []);

  const requestId = useRef({ id: 0 });
  const loadFolders = useCallback(async () => {
    const currentRequest = ++requestId.current.id;
    try {
      setLoading(true);

      const params: Parameters<typeof searchTitleFolders>[0] = {
        limit: ITEMS_PER_PAGE,
        offset: page * ITEMS_PER_PAGE,
      };

      if (searchQuery.trim()) {
        // Search by project number or title ID
        if (/^\d+$/.test(searchQuery.trim())) {
          params.projectNumber = searchQuery.trim();
        } else {
          params.titleId = searchQuery.trim();
        }
      }

      if (locationState !== "all") {
        params.locationState = locationState;
      }

      if (auditStatus !== "all") {
        params.auditStatus = auditStatus;
      }

      if (bucket.trim()) {
        params.bucket = bucket.trim();
      }

      const result = await searchTitleFolders(params);
      if (currentRequest !== requestId.current.id) return;
      if (page > 0 && result.rows.length === 0) {
        setPage(Math.max(0, Math.ceil(result.totalCount / ITEMS_PER_PAGE) - 1));
      }
      setFolders(result.rows);
      setTotalCount(result.totalCount);
    } catch (error: any) {
      if (currentRequest !== requestId.current.id) return;
      toast({
        title: "Error loading folders",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      if (currentRequest === requestId.current.id) setLoading(false);
    }
  }, [page, searchQuery, locationState, auditStatus, bucket, toast]);

  useEffect(() => {
    const requests = requestId.current;
    loadFolders();
    return () => { ++requests.id; };
  }, [loadFolders]);

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setLocationState("all");
    setAuditStatus("all");
    setBucket("");
    setPage(0);
  };

  const hasActiveFilters = searchQuery || locationState !== "all" || auditStatus !== "all" || bucket;

  return (
    <TitleFolderLayout onRefresh={loadFolders} isRefreshing={loading}>
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <FileStack className="h-6 w-6 text-amber-600" />
              Title Folders
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              Browse and search all indexed title folders
            </p>
          </div>
          <Badge variant="outline" className="text-slate-600 px-3 py-1.5 text-sm font-medium">
            {totalCount.toLocaleString()} total
          </Badge>
        </div>

        {/* Filters */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="flex flex-wrap gap-4">
                {/* Search Input */}
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search by project number or title ID..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setPage(0);
                      }}
                      className="pl-10 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                    />
                  </div>
                </div>

                {/* Location State Filter */}
                <Select
                  value={locationState}
                  onValueChange={(value) => {
                    setLocationState(value as LocationState | "all");
                    setPage(0);
                  }}
                >
                  <SelectTrigger className="w-[180px] bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                    <SelectValue placeholder="Location State" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATION_STATES.map((state) => (
                      <SelectItem key={state.value} value={state.value}>
                        <span className="flex items-center gap-2">
                          <state.icon className="h-4 w-4" />
                          {state.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Audit Status Filter */}
                <Select
                  value={auditStatus}
                  onValueChange={(value) => {
                    setAuditStatus(value as AuditStatus | "all");
                    setPage(0);
                  }}
                >
                  <SelectTrigger className="w-[180px] bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                    <SelectValue placeholder="Audit Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {AUDIT_STATUSES.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Bucket Filter */}
                <Input
                  placeholder="Bucket (e.g., 1000-1999)"
                  value={bucket}
                  onChange={(e) => {
                    setBucket(e.target.value);
                    setPage(0);
                  }}
                  className="w-[180px] bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                />

                {/* Actions */}
                <div className="flex gap-2">
                  <Button type="submit" className="bg-amber-600 hover:bg-amber-700 text-white">
                    <Filter className="h-4 w-4 mr-2" />
                    Apply
                  </Button>
                  {hasActiveFilters && (
                    <Button type="button" variant="outline" onClick={clearFilters}>
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Results Table */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                  <TableHead className="w-[100px] font-semibold text-slate-700 dark:text-slate-300">
                    Project #
                  </TableHead>
                  <TableHead className="font-semibold text-slate-700 dark:text-slate-300">
                    Title
                  </TableHead>
                  <TableHead className="font-semibold text-slate-700 dark:text-slate-300">
                    Author
                  </TableHead>
                  <TableHead className="w-[120px] font-semibold text-slate-700 dark:text-slate-300">
                    Location
                  </TableHead>
                  <TableHead className="w-[140px] font-semibold text-slate-700 dark:text-slate-300">
                    Audit Status
                  </TableHead>
                  <TableHead className="w-[120px] font-semibold text-slate-700 dark:text-slate-300">
                    Bucket
                  </TableHead>
                  <TableHead className="w-[100px] text-right font-semibold text-slate-700 dark:text-slate-300">
                    <span className="flex items-center justify-end gap-1">
                      <HardDrive className="h-3.5 w-3.5" />
                      Size
                    </span>
                  </TableHead>
                  <TableHead className="w-[100px] text-right font-semibold text-slate-700 dark:text-slate-300">
                    Last Audit
                  </TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="h-32 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent" />
                        <p className="mt-2 text-sm text-slate-500">Loading folders...</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : folders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="h-32 text-center">
                      <div className="flex flex-col items-center justify-center text-slate-500">
                        <FolderX className="h-10 w-10 mb-2 text-slate-300" />
                        <p className="font-medium">No folders found</p>
                        <p className="text-sm">Try adjusting your filters</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  folders.map((folder) => (
                    <TableRow
                      key={folder.titleId}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                    >
                      <TableCell className="font-mono text-sm font-medium text-slate-800 dark:text-slate-200">
                        {folder.projectNumber ?? "-"}
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-sm text-slate-700 dark:text-slate-300 truncate block max-w-[250px]">
                                {folder.titleName ?? "Unknown Title"}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{folder.titleName}</p>
                              <p className="text-xs text-slate-400 mt-1">ID: {folder.titleId}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 dark:text-slate-400">
                        {folder.authorName ?? "-"}
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-2">
                          {getLocationIcon(folder.locationState)}
                          <span className="text-sm text-slate-600 dark:text-slate-400">
                            {folder.locationState}
                          </span>
                        </span>
                      </TableCell>
                      <TableCell>{getAuditStatusBadge(folder.auditStatus)}</TableCell>
                      <TableCell>
                        <span className="text-sm font-mono text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                          {folder.currentBucket ?? folder.expectedBucket ?? "-"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          {formatBytes(folder.totalSizeBytes)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-sm text-slate-500 dark:text-slate-500">
                          {formatRelativeTime(folder.lastAuditedUtc)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {folder.currentWebUrl && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => window.open(folder.currentWebUrl!, "_blank")}
                                >
                                  <ExternalLink className="h-4 w-4 text-slate-400 hover:text-slate-600" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Open in SharePoint</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-800">
              <div className="text-sm text-slate-600 dark:text-slate-400">
                Showing {page * ITEMS_PER_PAGE + 1} to{" "}
                {Math.min((page + 1) * ITEMS_PER_PAGE, totalCount)} of {totalCount.toLocaleString()}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i;
                    } else if (page < 3) {
                      pageNum = i;
                    } else if (page > totalPages - 4) {
                      pageNum = totalPages - 5 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={page === pageNum ? "default" : "outline"}
                        size="sm"
                        className={`w-8 ${page === pageNum ? "bg-amber-600 hover:bg-amber-700" : ""}`}
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum + 1}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </TitleFolderLayout>
  );
}
